import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';

let initialized = false;

/**
 * Initialize app lifecycle listeners for session recovery.
 *
 * On app resume (from background on iOS/Android, or tab re-focus on web):
 *   1. Force-refreshes the Supabase JWT via getSession()
 *   2. Dispatches a global 'app-resumed' event so pages can re-validate data
 *
 * Call once at app startup in main.tsx.
 */
export function initAppLifecycle(): void {
  if (initialized) return;
  initialized = true;

  const handleResume = async () => {
    try {
      await supabase.auth.getSession();
    } catch (err) {
      console.warn('[AppLifecycle] Session refresh on resume failed:', err);
    }
    window.dispatchEvent(new Event('app-resumed'));
  };

  // Capacitor native: use App plugin for reliable foreground detection
  if (Capacitor.isNativePlatform()) {
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          handleResume();
        }
      });
    }).catch(() => {
      // Fallback to visibilitychange if App plugin unavailable
      addVisibilityListener(handleResume);
    });
  } else {
    // Web: use visibilitychange
    addVisibilityListener(handleResume);
  }
}

function addVisibilityListener(onResume: () => void) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onResume();
    }
  });
}
